import { useEffect, useMemo, useState } from 'react';
import { Pencil, Trash2, X } from 'lucide-react';
import type { Period } from '@casetrace/shared';
import { Button } from '../../components/ui/button';
import { useDeletePeriod } from '../../hooks/use-period-mutations';
import type { TimelinePositionEngine } from './use-timeline-position';
import type { StoryModel } from './story-model';
import { isoTimestampToDateInput } from './period-date-input';

interface PeriodsPanelProps {
  readonly caseId: string;
  readonly periods: readonly Period[];
  readonly model: StoryModel;
  readonly engine: TimelinePositionEngine;
  readonly canWrite: boolean;
  readonly onEdit: (period: Period) => void;
  readonly onClose: () => void;
}

const DATE_FMT = new Intl.DateTimeFormat('nb-NO', { dateStyle: 'medium' });

export function PeriodsPanel({
  caseId,
  periods,
  model,
  engine,
  canWrite,
  onEdit,
  onClose,
}: PeriodsPanelProps) {
  const deleteMutation = useDeletePeriod(caseId);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const sorted = useMemo(
    () => periods.slice().sort((a, b) => a.from.localeCompare(b.from) || a.id.localeCompare(b.id)),
    [periods],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const navigateToPeriod = (period: Period) => {
    const epochMs = Date.parse(period.from);
    if (!Number.isFinite(epochMs)) return;
    engine.setTarget(model.scale.toPosition(epochMs));
    onClose();
  };

  const handleDelete = async (periodId: string) => {
    try {
      await deleteMutation.mutateAsync(periodId);
      setConfirmDeleteId(null);
    } catch {
      // Feil vises via deleteMutation.isError
    }
  };

  return (
    <aside
      className="fixed inset-y-0 right-0 z-30 flex w-full max-w-sm flex-col border-l p-4 shadow-2xl"
      style={{ background: 'var(--color-surface)', borderColor: 'rgba(255,255,255,0.07)' }}
    >
      <PanelHeader onClose={onClose} />

      {sorted.length === 0 && (
        <p className="mt-4 text-sm text-text-tertiary">Ingen tidsslotter er opprettet ennå.</p>
      )}

      <ul className="mt-4 flex flex-1 flex-col gap-2 overflow-y-auto">
        {sorted.map((period) => (
          <PeriodRow
            key={period.id}
            period={period}
            canWrite={canWrite}
            confirmingDelete={confirmDeleteId === period.id}
            isDeleting={deleteMutation.isPending && confirmDeleteId === period.id}
            onNavigate={() => navigateToPeriod(period)}
            onEdit={() => onEdit(period)}
            onRequestDelete={() => setConfirmDeleteId(period.id)}
            onCancelDelete={() => setConfirmDeleteId(null)}
            onConfirmDelete={() => void handleDelete(period.id)}
          />
        ))}
      </ul>

      {deleteMutation.isError && (
        <p className="mt-2 text-xs text-danger/80">Kunne ikke slette tidsslotten.</p>
      )}

      <Button className="mt-4" variant="ghost" onClick={onClose}>
        Lukk
      </Button>
    </aside>
  );
}

function PanelHeader({ onClose }: { readonly onClose: () => void }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <h2 className="text-sm font-semibold text-text-primary">Tidsslotter</h2>
        <p className="mt-0.5 text-xs text-text-tertiary">Kontekstperioder langs tidslinjen.</p>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="rounded-md p-1 text-text-tertiary hover:bg-white/6 hover:text-text-secondary"
        aria-label="Lukk tidsslotter"
      >
        <X size={15} />
      </button>
    </div>
  );
}

interface PeriodRowProps {
  readonly period: Period;
  readonly canWrite: boolean;
  readonly confirmingDelete: boolean;
  readonly isDeleting: boolean;
  readonly onNavigate: () => void;
  readonly onEdit: () => void;
  readonly onRequestDelete: () => void;
  readonly onCancelDelete: () => void;
  readonly onConfirmDelete: () => void;
}

function PeriodRow({
  period,
  canWrite,
  confirmingDelete,
  isDeleting,
  onNavigate,
  onEdit,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
}: PeriodRowProps) {
  const fromLabel = formatPeriodDate(period.from);
  const toLabel = formatPeriodDate(period.to);

  return (
    <li className="rounded-lg border border-white/8 bg-white/[0.03] p-3">
      <button
        type="button"
        onClick={onNavigate}
        className="flex w-full items-start gap-2 text-left hover:opacity-90"
      >
        <span
          className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: period.color }}
          aria-hidden
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-text-primary">
            {period.label}
          </span>
          <span className="mt-0.5 block text-xs text-text-tertiary">
            {fromLabel} – {toLabel}
          </span>
        </span>
      </button>

      {canWrite && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {confirmingDelete ? (
            <>
              <span className="text-xs text-text-secondary">Slette «{period.label}»?</span>
              <Button
                type="button"
                variant="ghost"
                className="px-2 py-1 text-xs"
                onClick={onCancelDelete}
              >
                Avbryt
              </Button>
              <Button
                type="button"
                className="px-2 py-1 text-xs"
                disabled={isDeleting}
                onClick={onConfirmDelete}
              >
                {isDeleting ? 'Sletter…' : 'Slett'}
              </Button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onEdit}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-text-secondary hover:bg-white/6"
              >
                <Pencil size={12} />
                Rediger
              </button>
              <button
                type="button"
                onClick={onRequestDelete}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-danger/80 hover:bg-danger/10"
              >
                <Trash2 size={12} />
                Slett
              </button>
            </>
          )}
        </div>
      )}
    </li>
  );
}

function formatPeriodDate(iso: string): string {
  const day = isoTimestampToDateInput(iso);
  if (!day) return '—';
  const parsed = Date.parse(`${day}T12:00:00`);
  if (!Number.isFinite(parsed)) return day;
  return DATE_FMT.format(new Date(parsed));
}
